import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "@/config";
import { PortalButton } from "@/components/PortalButton";
import { cn } from "@/lib/utils";

const PRIMARY = ["/", "/about", "/product-categories", "/how-it-works", "/delivery"];
const PLATFORM = [
  { label: "Online Ordering", to: "/online-ordering" },
  { label: "Reporting & Invoices", to: "/reporting" },
  { label: "Price Match", to: "/price-match" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const primaryLinks = NAV_LINKS.filter((l) => PRIMARY.includes(l.to));
  const onDark = !scrolled;
  const isHome = location.pathname === "/";

  const linkCls = ({ isActive }) =>
    cn(
      "px-3 py-2 text-sm font-medium rounded-full transition-colors duration-200",
      onDark ? "text-cream/85 hover:text-white hover:bg-white/10" : "text-forest/80 hover:text-forest hover:bg-forest/5",
      isActive && (onDark ? "text-white bg-white/15" : "text-forest bg-forest/10")
    );

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300",
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-forest/15 shadow-sm" : "bg-transparent border-b border-transparent"
      )}
    >
      <nav className="max-w-[1440px] mx-auto px-5 md:px-10 h-[74px] flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" aria-label="Brampton Cash & Carry home" className="flex items-center shrink-0">
          <img
            src="/brand_logo.png"
            alt="Brampton Cash & Carry logo"
            className={cn(
              "w-auto rounded-lg object-contain transition-all duration-300",
              isHome && !scrolled ? "h-12 md:h-16" : "h-10 md:h-11",
              onDark ? "bg-white/95 p-2" : "bg-white p-1.5 border border-forest/10"
            )}
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden xl:flex items-center gap-0.5">
          {primaryLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`} className={linkCls}>
              {l.label}
            </NavLink>
          ))}
          {/* Platform dropdown */}
          <div className="relative" onMouseEnter={() => setPlatformOpen(true)} onMouseLeave={() => setPlatformOpen(false)}>
            <button
              data-testid="nav-platform"
              className={cn(
                "px-3 py-2 text-sm font-medium rounded-full inline-flex items-center gap-1 transition-colors",
                onDark ? "text-cream/85 hover:text-white hover:bg-white/10" : "text-forest/80 hover:text-forest hover:bg-forest/5"
              )}
            >
              Platform <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <AnimatePresence>
              {platformOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 top-full pt-2 w-56"
                >
                  <div className="rounded-2xl border border-forest/15 bg-white shadow-hard p-2">
                    {PLATFORM.map((p) => (
                      <NavLink key={p.to} to={p.to} data-testid={`nav-${p.to.slice(1)}`} className="block px-3 py-2 rounded-xl text-sm text-forest/80 hover:bg-sage/50 hover:text-forest transition-colors">
                        {p.label}
                      </NavLink>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <NavLink to="/contact" data-testid="nav-contact" className={linkCls}>Contact</NavLink>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2.5">
          <Link
            to="/contact"
            data-testid="contact-sales-btn"
            className={cn(
              "hidden lg:inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors duration-300",
              onDark ? "border-cream/40 text-cream hover:bg-cream hover:text-forest" : "border-forest/40 text-forest hover:bg-forest hover:text-butter"
            )}
          >
            <Phone className="h-4 w-4" /> Contact Sales
          </Link>
          <div className="hidden sm:block">
            <PortalButton variant="butter" testId="header-portal-btn">Login / Sign Up</PortalButton>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className={cn("xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl border", onDark ? "border-cream/40 text-cream" : "border-forest/25 text-forest")}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-cream border-b border-forest/15"
          >
            <div className="px-5 py-5 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  className={({ isActive }) =>
                    cn("px-4 py-3 rounded-xl text-[15px] font-medium text-forest/85 hover:bg-forest/5", isActive && "bg-forest/10 text-forest")
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-3 pt-4 border-t border-forest/15">
                <PortalButton variant="butter" className="w-full justify-center" testId="mobile-portal-btn">
                  Login / Sign Up
                </PortalButton>
                <p className="text-center text-xs text-forest/60 mt-2 font-mono-accent">
                  Create your account and start ordering online.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
